import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Client-side wishlist store backed by localStorage.
 *
 * Each entry is a lightweight snapshot so the drawer can paint instantly without a
 * network request. Live variant data (sizes / availability) is fetched lazily when the
 * drawer opens. Mirrors the RecentlyViewed pattern already used in the theme.
 *
 * @typedef {Object} WishlistItem
 * @property {string} id - The product id.
 * @property {string} handle - The product handle (used to fetch live data).
 * @property {string} title - The product title.
 * @property {string} url - The product url.
 * @property {string} image - A product image url.
 * @property {number} price - The price in the shop's minor units (cents).
 * @property {number} [compareAtPrice] - The compare-at price in minor units.
 * @property {string|null} variantId - The chosen variant (size), or null until picked.
 * @property {number} addedAt - Timestamp the item was added.
 */
export class Wishlist {
  /** @static @constant {string} localStorage key. */
  static #STORAGE_KEY = 'petitblush:wishlist';

  /**
   * Unique identity of a wishlist row: a product *plus* a specific size. A product can
   * appear multiple times — once per chosen variant — so the product id alone is not unique.
   * A `null` variantId is the "no size picked yet" row (added from a product card).
   * @param {string|number} id - The product id.
   * @param {string|number|null} [variantId] - The variant (size) id.
   * @returns {string} The composite key.
   */
  static key(id, variantId) {
    return `${String(id)}:${variantId == null ? '' : String(variantId)}`;
  }

  /**
   * @returns {WishlistItem[]} All wishlist items, newest first.
   */
  static getAll() {
    try {
      const raw = localStorage.getItem(this.#STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} id - The product id.
   * @param {string|null} [variantId] - The variant id. Omit to match any variant of the product.
   * @returns {boolean} Whether that exact product+variant row is in the wishlist.
   */
  static has(id, variantId) {
    const key = this.key(id, variantId);
    return this.getAll().some((item) => this.key(item.id, item.variantId) === key);
  }

  /**
   * @param {string} id - The product id.
   * @returns {boolean} Whether the product is in the wishlist under any (or no) size.
   */
  static hasProduct(id) {
    return this.getAll().some((item) => item.id === String(id));
  }

  /**
   * @returns {number} The number of rows in the wishlist.
   */
  static count() {
    return this.getAll().length;
  }

  /**
   * Adds a row to the wishlist (newest first). No-op if the same product+variant exists.
   * @param {WishlistItem} item
   */
  static add(item) {
    const items = this.getAll();
    const key = this.key(item.id, item.variantId);
    if (items.some((existing) => this.key(existing.id, existing.variantId) === key)) return;

    items.unshift({ ...item, id: String(item.id) });
    this.#save(items);
  }

  /**
   * Removes a single product+variant row from the wishlist.
   * @param {string} id - The product id.
   * @param {string|null} [variantId] - The variant id.
   */
  static remove(id, variantId) {
    const key = this.key(id, variantId);
    const items = this.getAll().filter((item) => this.key(item.id, item.variantId) !== key);
    this.#save(items);
  }

  /**
   * Removes every row of a product, regardless of size. Used by the card heart.
   * @param {string} id - The product id.
   */
  static removeProduct(id) {
    const items = this.getAll().filter((item) => item.id !== String(id));
    this.#save(items);
  }

  /**
   * Toggles a single product+variant row.
   * @param {WishlistItem} item
   * @returns {boolean} The new presence state (true = now in wishlist).
   */
  static toggle(item) {
    if (this.has(item.id, item.variantId)) {
      this.remove(item.id, item.variantId);
      return false;
    }
    this.add(item);
    return true;
  }

  /**
   * Sets the chosen variant (size) for a specific row, identified by its current variantId.
   * Dedupes: if the product already has a row for the new variant, the edited row is dropped
   * so the same size never appears twice.
   * @param {string} id - The product id.
   * @param {string|null} fromVariantId - The row's current variant id (null for the no-size row).
   * @param {string|null} toVariantId - The newly chosen variant id.
   */
  static setVariant(id, fromVariantId, toVariantId) {
    const items = this.getAll();
    const fromKey = this.key(id, fromVariantId);
    const item = items.find((entry) => this.key(entry.id, entry.variantId) === fromKey);
    if (!item) return;

    const nextVariant = toVariantId ? String(toVariantId) : null;
    const targetKey = this.key(id, nextVariant);

    // Already chosen — nothing to do.
    if (fromKey === targetKey) return;

    // The target size already exists as its own row: drop the row we were editing.
    if (items.some((entry) => this.key(entry.id, entry.variantId) === targetKey)) {
      this.#save(items.filter((entry) => entry !== item));
      return;
    }

    item.variantId = nextVariant;
    this.#save(items);
  }

  /**
   * Persists items and notifies listeners.
   * @param {WishlistItem[]} items
   */
  static #save(items) {
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new WishlistUpdateEvent(items));
  }
}

/**
 * Event fired whenever the wishlist changes (add / remove / variant pick / cross-tab sync).
 * @extends {Event}
 */
export class WishlistUpdateEvent extends Event {
  /** @param {WishlistItem[]} items */
  constructor(items) {
    super(WishlistUpdateEvent.eventName, { bubbles: true });
    this.detail = { items };
  }

  static eventName = 'wishlist:update';
}

// Keep tabs in sync: a write in another tab fires `storage`, which we re-broadcast locally.
window.addEventListener('storage', (event) => {
  if (event.key === 'petitblush:wishlist') {
    document.dispatchEvent(new WishlistUpdateEvent(Wishlist.getAll()));
  }
});

// Expose the store for non-module scripts (e.g. the Shop the Look section's inline script).
window.Wishlist = Wishlist;

/**
 * Toggle button shown on product cards and the product page. Reflects and mutates
 * wishlist membership for a single product.
 *
 * @extends Component
 */
class WishlistHeart extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#sync);

    // On the product page, keep the stored size in sync with the variant picker.
    if (this.dataset.context === 'product') {
      document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.#sync);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /** Builds a snapshot item from the element's data attributes. */
  get item() {
    const { dataset } = this;
    return {
      id: String(dataset.productId),
      handle: dataset.productHandle,
      title: dataset.productTitle,
      url: dataset.productUrl,
      image: dataset.productImage || '',
      price: Number(dataset.productPrice) || 0,
      priceFormatted: dataset.productPriceFormatted || '',
      compareAtPrice: dataset.productCompareAtPrice ? Number(dataset.productCompareAtPrice) : undefined,
      compareAtPriceFormatted: dataset.productCompareAtPriceFormatted || '',
      variantId: dataset.variantId ? String(dataset.variantId) : null,
      addedAt: Date.now(),
    };
  }

  /** @returns {boolean} Whether this heart is the product-page (per-size) variant. */
  get #isProductContext() {
    return this.dataset.context === 'product';
  }

  /**
   * Toggles wishlist membership. Bound declaratively via `on:click`.
   *
   * On the product page the heart represents the *currently selected size*, so toggling
   * adds/removes just that product+variant. On a card there is no size, so the heart
   * represents the whole product: toggling on adds a no-size row, toggling off clears
   * every size of that product.
   */
  toggle = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#isProductContext) {
      Wishlist.toggle(this.item);
      return;
    }

    if (Wishlist.hasProduct(this.dataset.productId)) {
      Wishlist.removeProduct(this.dataset.productId);
    } else {
      Wishlist.add(this.item);
    }
  };

  /** Reflects current membership into the button state. */
  #sync = () => {
    // Product page: filled only when *this size* is wishlisted. Card: filled when the
    // product is on the wishlist under any size.
    const active = this.#isProductContext
      ? Wishlist.has(this.dataset.productId, this.dataset.variantId)
      : Wishlist.hasProduct(this.dataset.productId);
    this.classList.toggle('is-active', active);

    const button = this.querySelector('button');
    if (button) {
      button.setAttribute('aria-pressed', String(active));
      const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    }
  };

  /**
   * Keep the heart in sync with the variant picker: as the selected size changes, the heart
   * reflects whether *that* size is wishlisted (so a second size can be added independently).
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId) !== String(this.dataset.productId)) return;

    const variantId = event.detail?.resource?.id;
    if (variantId) {
      this.dataset.variantId = String(variantId);
      this.#sync();
    }
  };
}

if (!customElements.get('wishlist-heart')) {
  customElements.define('wishlist-heart', WishlistHeart);
}

/**
 * Header icon that displays the wishlist count bubble.
 *
 * @extends Component
 */
class WishlistIcon extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#render);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.#render);
  }

  #render = () => {
    // No count bubble — the filled pink heart itself signals a non-empty wishlist.
    this.classList.toggle('wishlist-icon--has-items', Wishlist.count() > 0);
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

/**
 * Slide-out wishlist drawer. Renders saved items, lets the customer pick a size per item
 * (fetched live), and adds everything to the cart in one call.
 *
 * @extends DialogComponent
 */
class WishlistDrawerComponent extends DialogComponent {
  /** @type {Map<string, Promise<any>>} Caches product JSON per handle for the session. */
  #productCache = new Map();
  /** @type {Map<string, any>} Resolved product JSON for synchronous re-renders. */
  #productData = new Map();
  /** @type {string} Signature of the currently rendered item set (ids), to avoid needless re-renders. */
  #renderedKey = '';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#handleWishlistUpdate);
    this.addEventListener('click', this.#handleItemsClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.#handleWishlistUpdate);
  }

  /** Opens the drawer and (re)renders its contents. Bound via `on:click="/open"`. */
  open() {
    this.#render();
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  /**
   * Re-render while open — but only when the set of items actually changed (add / remove).
   * A variant (size) pick must NOT rebuild the list: that would yank the clicked element out
   * of the DOM and make the dialog's outside-click check close the drawer.
   */
  #handleWishlistUpdate = () => {
    if (!this.refs.dialog?.open) return;
    const key = Wishlist.getAll()
      .map((item) => item.id)
      .sort()
      .join(',');
    if (key !== this.#renderedKey) this.#render();
  };

  /**
   * Orders rows so every size of a product sits together (under each other), while keeping
   * products themselves newest-first. The first time a product appears fixes its group's spot.
   * @param {WishlistItem[]} items
   * @returns {WishlistItem[]}
   */
  #grouped(items) {
    const order = [];
    const groups = new Map();
    for (const item of items) {
      if (!groups.has(item.id)) {
        groups.set(item.id, []);
        order.push(item.id);
      }
      groups.get(item.id).push(item);
    }
    return order.flatMap((id) => groups.get(id));
  }

  /** Renders the full list of wishlist items into the drawer body. */
  #render() {
    const items = Wishlist.getAll();
    const root = this.querySelector('.wishlist-drawer__body');
    if (!root) return;

    // Keyed on product ids only: adding/removing a row re-renders, but picking a size on an
    // existing row does not (that would yank the clicked element out of the DOM).
    this.#renderedKey = items
      .map((item) => item.id)
      .sort()
      .join(',');
    this.classList.toggle('wishlist-drawer--empty', items.length === 0);

    if (items.length === 0) {
      root.innerHTML = `<p class="wishlist-drawer__empty-text">${this.dataset.emptyText || ''}</p>`;
      this.#toggleFooter(false);
      return;
    }

    const ordered = this.#grouped(items);
    root.innerHTML = ordered.map((item) => this.#itemTemplate(item)).join('');
    this.#toggleFooter(true);

    // Lazily hydrate each row's size picker from live product data.
    for (const item of ordered) {
      this.#hydrateSizes(item);
    }
  }

  /**
   * @param {WishlistItem} item
   * @returns {string} HTML for a single wishlist row.
   */
  #itemTemplate(item) {
    const money = item.priceFormatted || this.#formatMoney(item.price);
    const compare =
      item.compareAtPrice && item.compareAtPrice > item.price
        ? `<s class="wishlist-item__compare">${item.compareAtPriceFormatted || this.#formatMoney(item.compareAtPrice)}</s>`
        : '';

    const hasSize = Boolean(item.variantId);

    return `
      <li class="wishlist-item" data-key="${Wishlist.key(item.id, item.variantId)}" data-id="${item.id}" data-variant="${item.variantId || ''}">
        <a class="wishlist-item__media" href="${item.url}" tabindex="-1" aria-hidden="true">
          ${item.image ? `<img src="${item.image}" alt="" loading="lazy" width="120" height="150">` : ''}
        </a>
        <div class="wishlist-item__details">
          <a class="wishlist-item__title" href="${item.url}">${item.title}</a>
          <span class="wishlist-item__price">${money} ${compare}</span>
          <div class="wishlist-item__row">
            <div class="wishlist-item__sizes" data-handle="${item.handle}" data-selected="${item.variantId || ''}">
              <span class="wishlist-item__sizes-loading">${this.dataset.loadingText || ''}</span>
            </div>
            <button
              type="button"
              class="button wishlist-item__add"
              data-action="add-one"
              ${hasSize ? '' : 'disabled'}
            >${this.dataset.addToCartText || ''}</button>
          </div>
          <span class="wishlist-item__error" hidden>${this.dataset.chooseSizeText || ''}</span>
        </div>
        <button
          type="button"
          class="wishlist-item__remove button-unstyled"
          data-action="remove"
          aria-label="${this.dataset.removeText || ''}"
        >
          <span class="svg-wrapper"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg></span>
        </button>
      </li>
    `;
  }

  /**
   * Fetches live variant data and renders size pills for an item.
   * @param {WishlistItem} item
   */
  async #hydrateSizes(item) {
    const key = Wishlist.key(item.id, item.variantId);
    const row = this.querySelector(`.wishlist-item[data-key="${key}"]`);
    const container = row?.querySelector('.wishlist-item__sizes');
    if (!container) return;

    try {
      const product = await this.#fetchProduct(item.handle);
      if (!product || !Array.isArray(product.variants)) {
        container.innerHTML = '';
        return;
      }

      this.#productData.set(item.handle, product);

      // Single-variant products need no picker — auto-select the only variant.
      if (product.variants.length === 1) {
        const only = product.variants[0];
        if (item.variantId !== String(only.id)) {
          Wishlist.setVariant(item.id, item.variantId, String(only.id));
        }
        container.innerHTML = '';
        return;
      }

      this.#renderSizePicker(container, product, item);
    } catch {
      container.innerHTML = '';
    }
  }

  /**
   * Renders a size picker into a single item's container. When a size is already chosen
   * (and we're not editing), it collapses to the chosen size plus a "change" button.
   * @param {Element} container
   * @param {Object} product - The product JSON.
   * @param {WishlistItem} item
   * @param {boolean} [editing] - Force the full pill list even when a size is chosen.
   */
  #renderSizePicker(container, product, item, editing = false) {
    const row = container.closest('.wishlist-item');
    const sizeIndex = this.#sizeOptionIndex(product);
    const chosen = product.variants.find((variant) => String(variant.id) === String(item.variantId));

    // While the full picker is shown (editing, or no size yet) the "add to cart" button is
    // hidden — it returns once a size is chosen and the picker collapses.
    row?.classList.toggle('wishlist-item--picking', !chosen || editing);

    if (chosen && !editing) {
      container.innerHTML = `
        <span class="wishlist-size is-active wishlist-size--chosen">${this.#sizeLabel(chosen, sizeIndex)}</span>
        <button type="button" class="wishlist-size-change button-unstyled" data-action="edit-size" aria-label="${
          this.dataset.changeText || ''
        }">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.6"/><circle cx="12" cy="12" r="1.6"/><circle cx="19" cy="12" r="1.6"/></svg>
        </button>
      `;
      return;
    }

    container.innerHTML = product.variants
      .map((variant) => {
        const label = this.#sizeLabel(variant, sizeIndex);
        if (label === 'Default Title') return '';
        const isActive = String(variant.id) === String(item.variantId);
        return `
          <button
            type="button"
            class="wishlist-size${isActive ? ' is-active' : ''}"
            data-action="size"
            data-variant-id="${variant.id}"
            aria-pressed="${isActive}"
            ${variant.available ? '' : 'disabled'}
          >${label}</button>
        `;
      })
      .join('');
  }

  /**
   * @param {Object} variant
   * @param {number|null} sizeIndex
   * @returns {string} The size label for a variant.
   */
  #sizeLabel(variant, sizeIndex) {
    return sizeIndex === null ? variant.title : variant.options[sizeIndex];
  }

  /**
   * @param {Object} product - The product JSON.
   * @returns {number|null} Index of the size option, or null when there's no distinct size option.
   */
  #sizeOptionIndex(product) {
    const names = (product.options || []).map((opt) => (typeof opt === 'string' ? opt : opt.name));
    const index = names.findIndex((name) => ['size', 'maat', 'sizes'].includes(String(name).toLowerCase()));
    if (index !== -1) return index;
    return names.length === 1 ? 0 : null;
  }

  /**
   * @param {string} handle
   * @returns {Promise<any>} Cached product JSON.
   */
  #fetchProduct(handle) {
    if (!this.#productCache.has(handle)) {
      this.#productCache.set(
        handle,
        fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`).then((res) =>
          res.ok ? res.json() : null
        )
      );
    }
    return this.#productCache.get(handle);
  }

  /**
   * Delegated click handler for size pills, remove buttons and the add-all action.
   * @param {MouseEvent} event
   */
  #handleItemsClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!target) return;

    // Stop the dialog's outside-click handler (same element, registered later) from firing —
    // otherwise re-rendering a row would be mistaken for a click outside and close the drawer.
    event.stopImmediatePropagation();

    const { action } = target.dataset;
    const row = target.closest('.wishlist-item');

    if (action === 'remove' && row) {
      Wishlist.remove(row.dataset.id, row.dataset.variant || null);
      return;
    }

    if (action === 'size' && row) {
      const fromVariant = row.dataset.variant || null;
      const toVariant = target.dataset.variantId;
      Wishlist.setVariant(row.dataset.id, fromVariant, toVariant);

      // If picking this size collapsed onto an existing row, the list was rebuilt already.
      if (!this.isConnected || !this.contains(row)) return;

      // Otherwise reflect the new size on this row in place (no full rebuild).
      row.dataset.variant = toVariant;
      row.dataset.key = Wishlist.key(row.dataset.id, toVariant);
      row.querySelector('.wishlist-item__error')?.setAttribute('hidden', '');
      row.classList.remove('wishlist-item--needs-size');
      row.querySelector('.wishlist-item__add')?.removeAttribute('disabled');
      this.#refreshRowSizes(row, false);
      return;
    }

    if (action === 'edit-size' && row) {
      this.#refreshRowSizes(row, true);
      return;
    }

    if (action === 'add-one' && row) {
      this.#addOneToCart(row);
      return;
    }

    if (action === 'add-all') {
      this.#addAllToCart();
    }
  };

  /**
   * Re-renders a single row's size picker without rebuilding the whole list.
   * @param {Element} row
   * @param {boolean} editing
   */
  #refreshRowSizes(row, editing) {
    const container = row.querySelector('.wishlist-item__sizes');
    const key = Wishlist.key(row.dataset.id, row.dataset.variant || null);
    const item = Wishlist.getAll().find((entry) => Wishlist.key(entry.id, entry.variantId) === key);
    const product = item && this.#productData.get(item.handle);
    if (container && product && item) this.#renderSizePicker(container, product, item, editing);
  }

  /**
   * Adds a single row's chosen size to the cart. The row then leaves the wishlist (it's in
   * the cart now), the wishlist drawer closes and the cart opens — mirroring "add all".
   * @param {Element} row
   */
  async #addOneToCart(row) {
    const id = row.dataset.id;
    const variantId = row.dataset.variant;

    // No size yet — prompt for one instead of adding.
    if (!variantId) {
      row.classList.add('wishlist-item--needs-size');
      row.querySelector('.wishlist-item__error')?.removeAttribute('hidden');
      return;
    }

    const button = row.querySelector('.wishlist-item__add');
    const originalText = button?.textContent;
    if (button) {
      button.setAttribute('disabled', '');
      button.textContent = this.dataset.addingText || originalText || '';
    }

    try {
      const cartItems = document.querySelector('cart-items-component');
      const sectionId = cartItems?.dataset?.sectionId;

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionId ? [sectionId] : [],
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Cart add failed');
      const state = await response.json();

      await this.closeDialog();
      Wishlist.remove(id, variantId);

      document.dispatchEvent(
        new CartAddEvent(state, 'wishlist-drawer-component', {
          source: 'wishlist-drawer-component',
          itemCount: 1,
          sections: state.sections || {},
        })
      );
    } catch (error) {
      console.error('[Wishlist] Add to cart failed:', error);
      if (button) {
        button.removeAttribute('disabled');
        if (originalText) button.textContent = originalText;
      }
    }
  }

  /** Adds every item that has a chosen size to the cart, flagging those that don't. */
  async #addAllToCart() {
    const items = Wishlist.getAll();
    const ready = items.filter((item) => item.variantId);
    const missing = items.filter((item) => !item.variantId);

    // Flag rows still missing a size (match per row, since a product can have several rows).
    this.querySelectorAll('.wishlist-item').forEach((row) => {
      const hasSize = Boolean(row.dataset.variant);
      row.classList.toggle('wishlist-item--needs-size', !hasSize);
      const error = row.querySelector('.wishlist-item__error');
      if (error) error.toggleAttribute('hidden', hasSize);
    });

    if (ready.length === 0) {
      this.#scrollToFirstMissing();
      return;
    }

    const button = this.querySelector('[data-action="add-all"]');
    const originalText = button?.textContent;
    if (button) {
      button.setAttribute('disabled', '');
      button.textContent = this.dataset.addingText || originalText || '';
    }

    try {
      const cartItems = document.querySelector('cart-items-component');
      const sectionId = cartItems?.dataset?.sectionId;

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: ready.map((item) => ({ id: Number(item.variantId), quantity: 1 })),
          sections: sectionId ? [sectionId] : [],
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Cart add failed');
      const state = await response.json();

      await this.closeDialog();

      // Notify the rest of the theme so the cart icon + drawer update (and auto-open).
      document.dispatchEvent(
        new CartAddEvent(state, 'wishlist-drawer-component', {
          source: 'wishlist-drawer-component',
          itemCount: ready.length,
          sections: state.sections || {},
        })
      );
    } catch (error) {
      console.error('[Wishlist] Add all failed:', error);
    } finally {
      if (button) {
        button.removeAttribute('disabled');
        if (originalText) button.textContent = originalText;
      }
    }
  }

  #scrollToFirstMissing() {
    this.querySelector('.wishlist-item--needs-size')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /** @param {boolean} show */
  #toggleFooter(show) {
    const footer = this.querySelector('.wishlist-drawer__footer');
    if (footer) footer.toggleAttribute('hidden', !show);
  }

  /**
   * @param {number} cents
   * @returns {string} A formatted money string using the shop's currency.
   */
  #formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl', {
        style: 'currency',
        currency,
      }).format(amount);
    } catch {
      return `${amount.toFixed(2)} ${currency}`;
    }
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
